/**
 * enginePreProcess_Helper.ts - Helper functions for EnginePreProcess
 * Port of C# EnginePreProcess_Helper.cs
 * Pure ES Module version
 */

import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import { OperationsMetadata, PreprocessedTemplate } from '../../model/index.js';
import type { EnginePreProcess } from './enginePreProcess.js';
import type { JsonObject } from '../../app/json/jsonValue.js';
import type { TemplateOperation } from '../../model/templateOperation.js';
import type { InheritanceOperation } from '../../model/inheritanceOperation.js';

/**
 * Convert milliseconds to nanoseconds
 */
export function msToNanos(ms: number): number {
    return Math.round(ms * 1_000_000);
}

/**
 * Create flow stack for inheritance resolution
 */
export function createFlowStack(appFile: string): string[] {
    return [appFile];
}

/**
 * Get main template from loader
 */
export function getMainTemplate(engine: EnginePreProcess, appSite: string, appFile: string, appView: string | null, flowStack: string[], configJson: JsonObject | null = null, clientMode: boolean = false): { mainTemplate: PreprocessedTemplate; mainJson: JsonObject | null; resolvedTemplateName: string; resolvedAppSite: string; mainLatePlaceholders: string[] } {
    const startGetTemplate = performance.now();
    const { template: mainTemplate, json: mainJson, resolvedTemplateName, resolvedAppSite, viewHtml: wrappedHtml, latePlaceholders: mainLatePlaceholders } =
        engine._loader!.getTemplateWithView(appSite, appFile, appFile, appView, flowStack, configJson, clientMode);
    engine._getTemplateNanos += msToNanos(performance.now() - startGetTemplate);
    engine._getTemplateCount++;

    // Create new template with wrapped HTML (preserves preprocessed data, keeps cache immutable)
    const resultTemplate = new PreprocessedTemplate();
    resultTemplate.originalContent = wrappedHtml;
    resultTemplate.actualTemplateName = mainTemplate.actualTemplateName;
    resultTemplate.placeholders = mainTemplate.placeholders;
    resultTemplate.slottedTemplates = mainTemplate.slottedTemplates;
    resultTemplate.replacementMappings = mainTemplate.replacementMappings;
    resultTemplate.jsonArrayPlaceholders = mainTemplate.jsonArrayPlaceholders;

    return { mainTemplate: resultTemplate, mainJson, resolvedTemplateName, resolvedAppSite, mainLatePlaceholders };
}

/**
 * Merge main template with JSON
 */
export function mergeMainTemplateJson(engine: EnginePreProcess, mainTemplate: PreprocessedTemplate, mainJson: JsonObject | null, contentHtml: string, templateKey: string, appFile: string, operations: TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null): string {
    if (!mainJson) {
        return contentHtml;
    }

    // Only pass metadata if there is something to process (avoids overhead for simple templates).
    const arrayMetadata = (mainTemplate.jsonArrayPlaceholders && mainTemplate.jsonArrayPlaceholders.length > 0)
        ? mainTemplate.jsonArrayPlaceholders : null;
    const jsonKeyMetadata = (mainTemplate.jsonKeyPlaceholders && mainTemplate.jsonKeyPlaceholders.length > 0)
        ? mainTemplate.jsonKeyPlaceholders : null;
    const startJsonMerge = performance.now();
    const merged = JsonMergeUtil.mergeTemplateWithJson(
        contentHtml,
        mainJson,
        templateKey,
        appFile,
        operations,
        engine._loader!.maxArrayOperationItems,
        engine._loader!.defaultReplaceHtml,
        engine._loader!.defaultSkipJson,
        arrayMetadata,
        jsonKeyMetadata,
        contextJson,
        configJson
    );
    engine._jsonMergeNanos += msToNanos(performance.now() - startJsonMerge);
    engine._jsonMergeCount++;

    return merged;
}

/**
 * Build operations metadata
 */
export function buildOperationsMetadata(genOperationJson: boolean, appSite: string, appFile: string, appView: string | null, templateKey: string, operations: TemplateOperation[], inheritanceOps: InheritanceOperation[]): OperationsMetadata | null {
    if (!genOperationJson) {
        return null;
    }

    const opsMetadata = new OperationsMetadata();
    opsMetadata.appSite = appSite;
    opsMetadata.appFile = appFile;
    opsMetadata.appView = appView || '';
    opsMetadata.templateName = templateKey;
    opsMetadata.operations = operations;
    opsMetadata.hasInheritance = inheritanceOps.length > 0;
    opsMetadata.inheritanceOperations = inheritanceOps;

    return opsMetadata;
}

/**
 * Accumulate dynamic data timing from loader
 */
export function accumulateDynamicDataTimingFromLoader(engine: EnginePreProcess): void {
    const { nanos: dynamicDataNanos, count: dynamicDataCount } = engine._loader!.getDynamicDataTiming();
    engine.accumulateDynamicDataTiming(dynamicDataNanos, dynamicDataCount);
}

/**
 * Accumulate dynamic view timing from loader
 */
export function accumulateDynamicViewTimingFromLoader(engine: EnginePreProcess): void {
    const { nanos: dynamicViewNanos, count: dynamicViewCount } = engine._loader!.getDynamicViewTiming();
    engine.accumulateDynamicViewTiming(dynamicViewNanos, dynamicViewCount);
}

/**
 * Accumulate dynamic action timing from loader
 */
export function accumulateDynamicActionTimingFromLoader(engine: EnginePreProcess): void {
    const { nanos: dynamicActionNanos, count: dynamicActionCount } = engine._loader!.getDynamicActionTiming();
    engine.accumulateDynamicActionTiming(dynamicActionNanos, dynamicActionCount);
}

export function accumulateDynamicClientDataTimingFromLoader(engine: EnginePreProcess): void {
    const { nanos: dynamicClientDataNanos, count: dynamicClientDataCount } = engine._loader!.getDynamicClientDataTiming();
    engine.accumulateDynamicClientDataTiming(dynamicClientDataNanos, dynamicClientDataCount);
}
